/* De schermen vóór en naast het dashboard.
 *
 * Gebaseerd op greenvision-ems/web/src/ui/Staat.jsx, uitgebreid met het
 * onderscheid dat in het CRM telt: "geen toegang", "bestaat niet", "geen
 * metingen" en "fout" zijn vier verschillende dingen en krijgen vier
 * verschillende schermen. Geen daarvan is een lege grafiek.
 *
 * Sinds F9.12 staan ze binnen de CRM-layout, in het vak waar het dashboard zou
 * staan: geen eigen volle schermhoogte meer, en een terugknop alleen als de
 * omgeving er een aanreikt (de CRM-kop en de zijbalk staan er al).
 */

import type { ReactNode } from 'react';

const C = { bg: '#F1F5F2', card: '#FFFFFF', line: '#DEE6E2', ink: '#0B1F1A', t2: '#4E635C', t3: '#7C9089', brand: '#014336', warn: '#B4650C', fout: '#B3382A' };
const UI = '-apple-system, BlinkMacSystemFont, "Segoe UI", Inter, Roboto, Helvetica, Arial, sans-serif';
const MONO = 'ui-monospace, "SF Mono", Menlo, Consolas, monospace';

function Blad({ children, soort }: { children: ReactNode; soort: string }) {
  return (
    <div data-ems-staat={soort} style={{ background: C.bg, border: `1px solid ${C.line}`, borderRadius: 12, fontFamily: UI, color: C.ink, display: 'grid', placeItems: 'center', padding: 20 }}>
      <div role={soort === 'laden' ? 'status' : 'alert'} style={{ background: C.card, border: `1px solid ${C.line}`, borderRadius: 14, padding: 26, width: '100%', maxWidth: 620 }}>
        {children}
      </div>
    </div>
  );
}

function Kop({ children, kleur = C.t3 }: { children: ReactNode; kleur?: string }) {
  return <div style={{ fontSize: 10, letterSpacing: '.14em', textTransform: 'uppercase', color: kleur, fontWeight: 700 }}>{children}</div>;
}

function Titel({ children }: { children: ReactNode }) {
  return <div style={{ fontSize: 17, fontWeight: 700, letterSpacing: '-0.02em', marginTop: 8 }}>{children}</div>;
}

function Tekst({ children }: { children: ReactNode }) {
  return <div style={{ fontSize: 12.5, color: C.t2, marginTop: 9, lineHeight: 1.6 }}>{children}</div>;
}

function Knoppen({ opnieuw, terug, terugLabel }: { opnieuw?: () => void; terug?: () => void; terugLabel?: string }) {
  if (!opnieuw && !(terug && terugLabel)) return null;
  return (
    <div style={{ display: 'flex', gap: 9, marginTop: 18 }}>
      {opnieuw && (
        <button type="button" onClick={opnieuw} style={{ padding: '9px 14px', borderRadius: 8, border: 'none', background: C.brand, color: '#fff', fontSize: 12.5, fontWeight: 700, cursor: 'pointer' }}>Opnieuw proberen</button>
      )}
      {terug && terugLabel && (
        <button type="button" onClick={terug} style={{ padding: '9px 14px', borderRadius: 8, border: `1px solid ${C.line}`, background: '#fff', color: C.t2, fontSize: 12.5, fontWeight: 650, cursor: 'pointer' }}>{terugLabel}</button>
      )}
    </div>
  );
}

export function Laden({ tekst = 'EMS-data ophalen…' }: { tekst?: string }) {
  return <Blad soort="laden"><div style={{ fontSize: 13, color: C.t2 }}>{tekst}</div></Blad>;
}

/** Ingelogd, maar met een rol die geen policy op ems heeft, of een verlopen sessie. */
export function GeenToegang({ rol, detail, terug, terugLabel }: { rol?: string | null; detail?: string; terug?: () => void; terugLabel?: string }) {
  return (
    <Blad soort="geen-toegang">
      <Kop kleur={C.fout}>Geen toegang</Kop>
      <Titel>Geen toegang tot de energiemonitoring</Titel>
      <Tekst>
        Deze gegevens staan in het schema <code style={{ fontFamily: MONO }}>ems</code>. Daarop staan alleen policies voor de rollen
        admin, super_admin en crm_user.{rol ? <> Jouw rol is <b>{rol}</b>.</> : null} Er is niets opgehaald en niets getoond.
      </Tekst>
      {detail && <Tekst><span style={{ fontFamily: MONO, fontSize: 11.5 }}>{detail}</span></Tekst>}
      <Knoppen terug={terug} terugLabel={terugLabel} />
    </Blad>
  );
}

export function NietGevonden({ locatieId, detail, terug, terugLabel }: { locatieId?: string; detail?: string; terug?: () => void; terugLabel?: string }) {
  return (
    <Blad soort="niet-gevonden">
      <Kop>Locatie niet gevonden</Kop>
      <Titel>Deze locatie staat niet (actief) in ems.locatie</Titel>
      <Tekst>
        Je sessie mag ems lezen — dat is gecontroleerd met dezelfde has_role() als de policy — maar er is geen actieve
        locatie met id <code style={{ fontFamily: MONO }}>{locatieId}</code>.
      </Tekst>
      {detail && <Tekst><span style={{ fontFamily: MONO, fontSize: 11.5 }}>{detail}</span></Tekst>}
      <Knoppen terug={terug} terugLabel={terugLabel} />
    </Blad>
  );
}

export function Fout({ fout, opnieuw, terug, terugLabel }: { fout: any; opnieuw: () => void; terug?: () => void; terugLabel?: string }) {
  const soort: string = fout?.soort || 'fout';
  const kop = soort === 'configuratie' ? 'Configuratie' : soort === 'paginering' ? 'Onvolledig opgehaald' : 'Ophalen mislukt';
  const titel = soort === 'configuratie'
    ? 'De Data API kan niet bij het schema ems'
    : soort === 'paginering'
      ? 'Niet alle metingen zijn binnengekomen, dus er wordt niets getekend'
      : 'Er is een fout opgetreden, er wordt niets getekend';
  return (
    <Blad soort={soort}>
      <Kop kleur={C.warn}>{kop}</Kop>
      <Titel>{titel}</Titel>
      <Tekst><span style={{ fontFamily: MONO, fontSize: 11.5 }}>{fout?.message || String(fout)}</span></Tekst>
      <Knoppen opnieuw={opnieuw} terug={terug} terugLabel={terugLabel} />
    </Blad>
  );
}

export function GeenMetingen({ locatie, telling, opnieuw }: {
  locatie: string;
  telling: { van: string; tot: string; dagen: number; assetsZichtbaar: number; metingen: number };
  opnieuw: () => void;
}) {
  return (
    <Blad soort="geen-metingen">
      <Kop>Nog geen metingen</Kop>
      <Titel>{locatie} staat er, de metingen in dit venster niet</Titel>
      <Tekst>
        Van {telling.van} tot en met {telling.tot} ({telling.dagen} dagen) staat er geen enkele rij pv_vermogen in
        ems.meting voor de {telling.assetsZichtbaar} assets van deze locatie. De locatie en de assets zijn wél gelezen, en
        ems.meting heeft (gemeten 12-09-2026) dezelfde policy als die twee. Dit wijst dus op een lege reeks en niet op een
        weigering: kijk bij de ingest (ems.ingest_run).
      </Tekst>
      <Tekst>
        <span style={{ fontFamily: MONO, fontSize: 11.5 }}>assets zichtbaar: {telling.assetsZichtbaar} · rijen geteld: {telling.metingen}</span>
      </Tekst>
      <Knoppen opnieuw={opnieuw} />
    </Blad>
  );
}

/** Een oude link /crm/ems/:locatieId naar een id die niet (meer) in ems.locatie staat. */
export function EmsBestaatNiet({ locatieId, terug, terugLabel }: { locatieId?: string; terug?: () => void; terugLabel?: string }) {
  return (
    <Blad soort="bestaat-niet">
      <Kop>Energiemonitor niet gevonden</Kop>
      <Titel>Deze energiemonitor bestaat niet (meer)</Titel>
      <Tekst>
        Er staat geen energiemonitor met id <code style={{ fontFamily: MONO }}>{locatieId}</code> in ems.locatie. De link is
        oud of verkeerd overgenomen, of de energiemonitor is weggehaald. Het EMS van een locatie open je voortaan vanuit
        Locaties.
      </Tekst>
      <Knoppen terug={terug} terugLabel={terugLabel} />
    </Blad>
  );
}

/** Een oude link /crm/ems/:locatieId naar een energiemonitor zonder site_id. */
export function EmsZonderSite({ locatieId, terug, terugLabel }: { locatieId?: string; terug?: () => void; terugLabel?: string }) {
  return (
    <Blad soort="zonder-site">
      <Kop>Nog niet gekoppeld</Kop>
      <Titel>Deze energiemonitor hangt nog niet aan een locatie</Titel>
      <Tekst>
        De energiemonitor <code style={{ fontFamily: MONO }}>{locatieId}</code> staat in ems.locatie, maar zijn site_id is
        leeg. Het dashboard staat op de pagina van een locatie in Locaties, en zolang die koppeling ontbreekt is het daar
        niet te openen. Koppelen gaat nu alleen via Greenvision-beheer.
      </Tekst>
      <Knoppen terug={terug} terugLabel={terugLabel} />
    </Blad>
  );
}

/** Er is een site, maar geen EMS-locatie die eraan hangt, ook geen inactieve. */
export function GeenEms({ siteNaam }: { siteNaam?: string }) {
  return (
    <Blad soort="geen-ems">
      <Kop>Energiemonitoring</Kop>
      <Titel>Nog geen energiemonitoring ingericht voor deze locatie</Titel>
      <Tekst>
        Je sessie mag ems lezen — gecontroleerd met dezelfde has_role() als de policy — maar er is geen rij in
        ems.locatie met site_id naar {siteNaam ? <b>{siteNaam}</b> : 'deze locatie'}. Zodra die er is, staat hier het dashboard.
      </Tekst>
    </Blad>
  );
}

/** Er hangen alleen inactieve EMS-locaties aan de site. */
export function EmsUit({ siteNaam, aantal }: { siteNaam?: string; aantal: number }) {
  return (
    <Blad soort="ems-uit">
      <Kop kleur={C.warn}>Energiemonitoring uit</Kop>
      <Titel>De energiemonitoring van deze locatie staat uit</Titel>
      <Tekst>
        Aan {siteNaam ? <b>{siteNaam}</b> : 'deze locatie'} {aantal === 1 ? 'hangt een energiemonitor' : <>hangen {aantal} energiemonitors</>} in
        ems.locatie, maar {aantal === 1 ? 'die staat' : 'die staan'} op inactief. Er wordt dan niets getoond. Zodra er één
        weer actief is, staat hier het dashboard; aan- en uitzetten gaat nu alleen via Greenvision-beheer.
      </Tekst>
    </Blad>
  );
}
